import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "../../components/Seo";
import { EmptyState, LoadMore, usePagedList } from "../../components/lawyer/LawyerUi";
import LawyerCard, { LawyerCardSkeleton } from "../../components/user/LawyerCard";
import { userService } from "../../services/userApi";

const toOptions = (list) => (Array.isArray(list) ? list : []).map((x) => ({ value: String(x.id), label: x.name }));

const FILTERS = [
  { key: "", label: "All" },
  { key: "Company", label: "Company" },
  { key: "Individual", label: "Individual" },
];

// Chip that drops down a searchable checklist. Picks apply straight away.
const FilterDropdown = ({ label, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => !rootRef.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const summary =
    value.length === 1 ? options.find((o) => o.value === value[0])?.label || label : value.length ? `${label} (${value.length})` : label;
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <div className="usp-dd" ref={rootRef}>
      <button
        type="button"
        className={`usp-chip usp-dd-btn${value.length ? " active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="usp-dd-summary">{summary}</span>
        <i className={`bi bi-chevron-${open ? "up" : "down"}`} />
      </button>
      {open && (
        <div className="usp-dd-menu">
          {options.length > 6 && (
            <input
              className="usp-dd-search"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          <ul role="listbox" aria-multiselectable="true" aria-label={label}>
            {shown.length === 0 ? (
              <li className="usp-dd-empty">No matches</li>
            ) : (
              shown.map((o) => {
                const on = value.includes(o.value);
                return (
                  <li key={o.value}>
                    <button type="button" role="option" aria-selected={on} className={on ? "on" : ""} onClick={() => toggle(o.value)}>
                      <i className={`bi ${on ? "bi-check-square-fill" : "bi-square"}`} />
                      <span>{o.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {value.length > 0 && (
            <button type="button" className="usp-dd-clear" onClick={() => onChange([])}>
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// "Hire Lawyers" / "See All" — every listed expert, searchable by name or
// practice area and filterable by firm vs individual lawyer, category,
// jurisdiction and who is online right now.
const UserLawyers = () => {
  const navigate = useNavigate();
  // ?category=<id> preselects a category (e.g. from a marketing banner).
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [online, setOnline] = useState(false);
  const [categoryIds, setCategoryIds] = useState(() => {
    const preset = searchParams.get("category");
    return preset ? [preset] : [];
  });
  const [jurisdictionIds, setJurisdictionIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([userService.getCategories(), userService.getDropdownData()]).then(([cats, dd]) => {
      if (!active) return;
      const c = cats.status === "fulfilled" && cats.value?.data?.status ? cats.value.data.data : [];
      setCategories(toOptions(Array.isArray(c) ? c : c?.categories));
      const d = dd.status === "fulfilled" && dd.value?.data?.status ? dd.value.data.data : {};
      setJurisdictions(toOptions(d.jurisdictions));
    });
    return () => {
      active = false;
    };
  }, []);

  // Debounce typing so each keystroke doesn't hit the API.
  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  const list = usePagedList(
    (page) =>
      userService.getLawyers(page, search, type, { categories: categoryIds, jurisdictions: jurisdictionIds, online }),
    (d) => d.lawyers,
    [search, type, online, categoryIds, jurisdictionIds]
  );
  const filtered = !!(search || online || categoryIds.length || jurisdictionIds.length);

  return (
    <div className="lwp-page usp-lawyers">
      <Seo title="Hire Lawyers | Legal Platform" noindex />

      <div className="usp-search-head">
        <button type="button" className="lwp-back" onClick={() => navigate(-1)} aria-label="Back">
          <i className="bi bi-chevron-left" />
        </button>
        <div className="usp-search">
          <i className="bi bi-search" />
          <input
            className="lwp-input"
            placeholder="Search Anything.."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search lawyers"
          />
        </div>
      </div>

      <div className="usp-chips usp-filter-row">
        <div className="usp-chip-group" role="tablist" aria-label="Lawyer type">
          {FILTERS.map((f) => (
            <button
              key={f.label}
              type="button"
              role="tab"
              aria-selected={type === f.key}
              className={`usp-chip${type === f.key ? " active" : ""}`}
              onClick={() => setType(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-pressed={online}
          className={`usp-chip usp-chip-online${online ? " active" : ""}`}
          onClick={() => setOnline((o) => !o)}
        >
          <span className="usp-online-dot" /> Online now
        </button>
        <FilterDropdown label="Categories" options={categories} value={categoryIds} onChange={setCategoryIds} />
        <FilterDropdown label="Jurisdictions" options={jurisdictions} value={jurisdictionIds} onChange={setJurisdictionIds} />
      </div>

      {list.loading ? (
        <div className="usp-lawyer-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <LawyerCardSkeleton key={i} />
          ))}
        </div>
      ) : list.meta.error ? (
        <EmptyState icon="bi-exclamation-circle" text={list.meta.error} />
      ) : list.items.length === 0 ? (
        <EmptyState icon="bi-person-x" text={filtered ? "No lawyers match your filters." : "No lawyers available right now."} />
      ) : (
        <>
          <div className="usp-lawyer-grid">
            {list.items.map((l) => (
              <Link key={l.id} to={`/user/lawyers/${l.id}`} className="usp-lawyer-link">
                <LawyerCard lawyer={l} />
              </Link>
            ))}
          </div>
          <LoadMore list={list} auto />
        </>
      )}
    </div>
  );
};

export default UserLawyers;
